import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { StudentResult } from '../types';
import { blankResult } from '../constant';
import { useMainContainerProvider } from '../../../../mainContainerProvider';
import useFetchWithAuth from '../../../../CommonHooks/useFetchWithAuth';
import { errorToast, successToast } from '../../../../CommonComponent/toast';
import { cloneDeep } from 'lodash';

interface ManageResultContextType {
  setLocalResultData: React.Dispatch<React.SetStateAction<StudentResult[]>>;
  localResultData: StudentResult[];
  setFilterRsultData: React.Dispatch<React.SetStateAction<StudentResult[] | undefined>>;
  filterRsultData: StudentResult[] | undefined;
  setNewResult: React.Dispatch<React.SetStateAction<StudentResult>>;
  newResult: StudentResult;
  isLoading: boolean;
  handlePostResult: () => Promise<void>;
  handleSearch: (className?: string, section?: string, examYear?: string, admissionNumber?: string) => Promise<void>;
  isSearchLoading: boolean;
  handleEditResult: () => Promise<void>;
  isEditLoading: boolean;
}

const ManageResultContext = createContext<ManageResultContextType>({} as ManageResultContextType);

const ManageResultProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [localResultData, setLocalResultData] = useState<StudentResult[]>([]);
  const [filterRsultData, setFilterRsultData] = useState<StudentResult[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const [isEditLoading, setIsEditLoading] = useState<boolean>(false);
  const [newResult, setNewResult] = useState<StudentResult>(blankResult);

  const { uid, userType } = useMainContainerProvider();
  const { fetchWithAuth } = useFetchWithAuth();

  const handlePostResult = async () => {
    setIsLoading(true);
    const payload = cloneDeep(newResult);

    try {
      const { error, data } = await fetchWithAuth(`add-result/`, {
        method: 'POST',
        body: JSON.stringify({ ...payload, uid, userType: userType }),
      });

      setIsLoading(false);
      if (error) {
        errorToast(error);
        setIsLoading(false);
      } else if (data) {
        successToast('Result added successfully!');
        setNewResult(blankResult);
        setIsLoading(false);
      }
    } catch (error) {
      errorToast('Failed to save result. Please contact the administrator.');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditResult = async () => {
    setIsEditLoading(true);
    const payload = cloneDeep(newResult);

    try {
      const { error, data } = await fetchWithAuth(`result/`, {
        method: 'POST',
        body: JSON.stringify({ ...payload, uid }),
      });
      if (error) {
        errorToast(error);
      } else if (data) {
        successToast('Result edited successfully!');
        setNewResult(blankResult);
      }
    } catch (error) {
      errorToast('Failed to save result. Please contact the administrator.');
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSearch = async (className?: string, section?: string, examYear?: string, rollNo?: string) => {
    setIsSearchLoading(true);
    try {
      const url = `result/?uid=${uid}&userType=${userType}&class=${className}&section=${section}&examYear=${examYear}&rollNo=${rollNo}`;
      const { error, data } = await fetchWithAuth(url);

      if (error) {
        errorToast(error);
      } else {
        setFilterRsultData(data);
      }
    } catch (error) {
      errorToast('Search failed.');
    } finally {
      setIsSearchLoading(false);
    }
  };

  return (
    <ManageResultContext.Provider
      value={{
        setLocalResultData,
        localResultData,
        setFilterRsultData,
        filterRsultData,
        setNewResult,
        newResult,
        isLoading,
        handlePostResult,
        handleSearch,
        isSearchLoading,
        handleEditResult,
        isEditLoading,
      }}
    >
      {children}
    </ManageResultContext.Provider>
  );
};

export const useManageResult = () => {
  const context = useContext(ManageResultContext);
  if (!context) {
    throw new Error('useManageResult must be used within a ManageResultProvider');
  }
  return context;
};

export default ManageResultProvider;
